import {
  Button,
  IconButton,
  SvgIcon,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { RiDeleteBinLine } from '@remixicon/react';
import { useLanguage } from '../../../contexts/language';
import { EntityStatus } from '../../../types/core';

export interface JourneyTemplateActionsProps {
  journeyId: string | null;
  isAdhoc: boolean;
  entityStatus: EntityStatus;
  isFetching: boolean;
  isOperator: boolean;
  onCancelRequest: () => void;
  onSubmitRequest: () => void;
  onUnsubmitRequest: () => void;
  onDeleteRequest: () => void;
  onRejectRequest: () => void;
  onApproveRequest: () => void;
  hasValidationErrors?: boolean;
}

const JourneyTemplateActions = ({
  journeyId,
  isAdhoc,
  entityStatus,
  isFetching,
  isOperator,
  onCancelRequest,
  onSubmitRequest,
  onUnsubmitRequest,
  onDeleteRequest,
  onRejectRequest,
  onApproveRequest,
  hasValidationErrors = false,
}: JourneyTemplateActionsProps) => {
  const { t } = useLanguage();

  const areActionsEnabled = !isFetching;
  const isCancellable = [EntityStatus.NotLogged, EntityStatus.Logged].includes(
    entityStatus,
  );

  const isDeletable =
    (isCancellable || entityStatus === EntityStatus.Rejected) && isAdhoc;

  const showSubmitAction = isCancellable && journeyId;

  const isMdUp = useMediaQuery('(min-width:768px)');

  const showUnsubmitAction =
    !isOperator && journeyId && entityStatus === EntityStatus.Submitted;

  const needsApproveOrReject =
    !isOperator && entityStatus === EntityStatus.PendingApproval;

  return (
    <div className="flex w-full md:w-auto flex-col md:flex-row gap-2 md:gap-4">
      {isCancellable && (
        <Button
          type="button"
          variant="outlined"
          color="error"
          onClick={onCancelRequest}
          disabled={!areActionsEnabled}
          fullWidth={!isMdUp}
          size={isMdUp ? 'medium' : 'small'}
        >
          {t('content.cancel-journey')}
        </Button>
      )}
      {showSubmitAction && (
        <Button
          type="button"
          variant="contained"
          onClick={onSubmitRequest}
          disabled={hasValidationErrors}
          fullWidth={!isMdUp}
          size={isMdUp ? 'medium' : 'small'}
        >
          {t('content.submit')}
        </Button>
      )}
      {showUnsubmitAction && (
        <Button
          type="button"
          variant="contained"
          onClick={onUnsubmitRequest}
          fullWidth={!isMdUp}
          size={isMdUp ? 'medium' : 'small'}
        >
          {t('content.unsubmit')}
        </Button>
      )}
      {isDeletable && (
        <div className="md:self-center">
          <Tooltip title={t(`content.delete`)}>
            <IconButton
              onClick={onDeleteRequest}
              disabled={!areActionsEnabled}
              sx={{
                width: isMdUp ? 'auto' : 48,
                alignSelf: isMdUp ? 'auto' : 'flex-end',
              }}
            >
              <SvgIcon component={RiDeleteBinLine} />
            </IconButton>
          </Tooltip>
        </div>
      )}
      {needsApproveOrReject && (
        <>
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onRejectRequest}
              disabled={isFetching}
            >
              {t('content.reject')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={onApproveRequest}
              loading={isFetching}
              disabled={isFetching}
            >
              {t('content.approve')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default JourneyTemplateActions;
